import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Eye, Send, Sparkles } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { humanizeError } from '@/lib/apiClient';
import {
  useCreateCampaign,
  usePreviewAudience,
  useTemplates,
  useTestSendCampaign,
} from '../hooks';
import type {
  AudienceFilters,
  CampaignTargeting,
  CreateCampaignBody,
} from '../api/communicationApi';

type Channel = 'push' | 'in_app';
type TargetType = 'all' | 'userIds';
type ContentMode = 'template' | 'custom';

interface FormState {
  name: string;
  mode: ContentMode;
  templateSlug: string;
  channels: Record<Channel, boolean>;
  pushTitle: string;
  pushBody: string;
  inAppTitle: string;
  inAppBody: string;
  targetRoute: string;
  // Audience
  targetType: TargetType;
  userIdsInput: string;
  filters: AudienceFilters;
  // Variables
  variables: Record<string, string>;
}

const INITIAL: FormState = {
  name: '',
  mode: 'custom',
  templateSlug: '',
  channels: { push: true, in_app: true },
  pushTitle: '',
  pushBody: '',
  inAppTitle: '',
  inAppBody: '',
  targetRoute: '',
  targetType: 'all',
  userIdsInput: '',
  filters: {},
  variables: {},
};

function extractVariables(text: string): string[] {
  const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  const out = new Set<string>();
  for (const m of text.matchAll(re)) out.add(m[1]);
  return Array.from(out);
}

function resolveVariables(text: string, vars: Record<string, string>): string {
  return text.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, k) => vars[k] ?? `{{${k}}}`);
}

export function CampaignNewPage() {
  const navigate = useNavigate();
  const templates = useTemplates(false);
  const createMut = useCreateCampaign();
  const previewMut = usePreviewAudience();
  const testSendMut = useTestSendCampaign();

  const [form, setForm] = React.useState<FormState>(INITIAL);
  const [previewResult, setPreviewResult] = React.useState<{ matchCount: number } | null>(null);
  const [lastCreatedId, setLastCreatedId] = React.useState<string | null>(null);
  const [testUserIds, setTestUserIds] = React.useState<string>('');

  const selectedTemplate = React.useMemo(() => {
    if (form.mode !== 'template' || !form.templateSlug) return null;
    return (templates.data?.templates ?? []).find((t) => t.slug === form.templateSlug) ?? null;
  }, [form.mode, form.templateSlug, templates.data]);

  // When a template is selected, prefill content from it.
  React.useEffect(() => {
    if (!selectedTemplate) return;
    setForm((f) => ({
      ...f,
      pushTitle: selectedTemplate.content.push?.title ?? '',
      pushBody: selectedTemplate.content.push?.body ?? '',
      inAppTitle: selectedTemplate.content.inApp?.title ?? '',
      inAppBody: selectedTemplate.content.inApp?.body ?? '',
      channels: {
        push: selectedTemplate.defaultChannels.includes('push'),
        in_app: selectedTemplate.defaultChannels.includes('in_app'),
      },
      targetRoute: selectedTemplate.targetRoute ?? f.targetRoute,
    }));
  }, [selectedTemplate]);

  const variableKeys = React.useMemo(() => {
    const pool = [form.pushTitle, form.pushBody, form.inAppTitle, form.inAppBody].join(' ');
    return extractVariables(pool);
  }, [form.pushTitle, form.pushBody, form.inAppTitle, form.inAppBody]);

  const resolvedContent = {
    push: form.channels.push
      ? {
          title: resolveVariables(form.pushTitle, form.variables),
          body: resolveVariables(form.pushBody, form.variables),
        }
      : undefined,
    inApp: form.channels.in_app
      ? {
          title: resolveVariables(form.inAppTitle, form.variables),
          body: resolveVariables(form.inAppBody, form.variables),
        }
      : undefined,
  };

  const canSubmit =
    form.name.trim().length > 0 &&
    (form.channels.push || form.channels.in_app) &&
    (!form.channels.push || (form.pushTitle.trim().length > 0 && form.pushBody.trim().length > 0)) &&
    (!form.channels.in_app ||
      (form.inAppTitle.trim().length > 0 && form.inAppBody.trim().length > 0)) &&
    (form.targetType === 'all' ||
      form.userIdsInput
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean).length > 0);

  const buildPayload = (): CreateCampaignBody => {
    const channels: Channel[] = [];
    if (form.channels.push) channels.push('push');
    if (form.channels.in_app) channels.push('in_app');

    let targeting: CampaignTargeting;
    if (form.targetType === 'userIds') {
      targeting = {
        type: 'userIds',
        userIds: form.userIdsInput
          .split(/[,\s]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        filters: Object.keys(form.filters).length > 0 ? form.filters : undefined,
      };
    } else {
      targeting = {
        type: 'all',
        filters: Object.keys(form.filters).length > 0 ? form.filters : undefined,
      };
    }

    return {
      name: form.name.trim(),
      content: {
        push: resolvedContent.push,
        inApp: resolvedContent.inApp,
      },
      channels,
      targeting,
      targetRoute: form.targetRoute.trim() || undefined,
      templateSlug: form.mode === 'template' ? form.templateSlug : undefined,
    };
  };

  return (
    <div className="space-y-6">
      <Link
        to="/communication"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to communication
      </Link>

      <PageHeader
        title="New campaign"
        description="Push + in-app inbox, custom or template content, optional audience filters."
      />

      {createMut.isError && (
        <ErrorState
          title="Create failed"
          description={humanizeError(createMut.error)}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Basics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="name">Campaign name</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={200}
              placeholder="e.g. April reactivation — inactive 7d"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Content mode</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={form.mode === 'custom' ? 'default' : 'outline'}
                onClick={() => setForm({ ...form, mode: 'custom', templateSlug: '' })}
              >
                Custom
              </Button>
              <Button
                type="button"
                size="sm"
                variant={form.mode === 'template' ? 'default' : 'outline'}
                onClick={() => setForm({ ...form, mode: 'template' })}
              >
                Template
              </Button>
            </div>
          </div>

          {form.mode === 'template' && (
            <div className="space-y-1.5">
              <Label>Template</Label>
              {templates.isLoading ? (
                <LoadingState inline />
              ) : (
                <Select
                  value={form.templateSlug}
                  onValueChange={(v) => setForm({ ...form, templateSlug: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(templates.data?.templates ?? []).map((t) => (
                      <SelectItem key={t.templateId} value={t.slug}>
                        {t.name} ({t.slug}) — {t.category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Channels</Label>
            <div className="flex flex-wrap gap-3 text-sm">
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={form.channels.push}
                  onChange={(e) =>
                    setForm({ ...form, channels: { ...form.channels, push: e.target.checked } })
                  }
                />
                Push
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={form.channels.in_app}
                  onChange={(e) =>
                    setForm({ ...form, channels: { ...form.channels, in_app: e.target.checked } })
                  }
                />
                In-app inbox
              </label>
            </div>
          </div>

          {form.channels.push && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="push-title">Push title (≤100)</Label>
                <Input
                  id="push-title"
                  value={form.pushTitle}
                  maxLength={100}
                  onChange={(e) => setForm({ ...form, pushTitle: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="push-body">Push body (≤500)</Label>
                <Textarea
                  id="push-body"
                  value={form.pushBody}
                  maxLength={500}
                  rows={3}
                  onChange={(e) => setForm({ ...form, pushBody: e.target.value })}
                />
              </div>
            </div>
          )}

          {form.channels.in_app && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="inapp-title">In-app title (≤100)</Label>
                <Input
                  id="inapp-title"
                  value={form.inAppTitle}
                  maxLength={100}
                  onChange={(e) => setForm({ ...form, inAppTitle: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inapp-body">In-app body (≤2000)</Label>
                <Textarea
                  id="inapp-body"
                  value={form.inAppBody}
                  maxLength={2000}
                  rows={4}
                  onChange={(e) => setForm({ ...form, inAppBody: e.target.value })}
                />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="route">Target route (optional deep link)</Label>
            <Input
              id="route"
              value={form.targetRoute}
              onChange={(e) => setForm({ ...form, targetRoute: e.target.value })}
              placeholder="e.g. CheckIn"
            />
          </div>
        </CardContent>
      </Card>

      {variableKeys.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4" /> Variables
            </CardTitle>
            <CardDescription>
              Values fill in every <code className="text-xs">{'{{placeholder}}'}</code> found above.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {variableKeys.map((k) => (
              <div key={k} className="grid grid-cols-[120px_1fr] items-center gap-3">
                <Label htmlFor={`var-${k}`} className="font-mono text-xs">
                  {k}
                </Label>
                <Input
                  id={`var-${k}`}
                  value={form.variables[k] ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, variables: { ...form.variables, [k]: e.target.value } })
                  }
                />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Audience</CardTitle>
          <CardDescription>
            Target all users (optionally filtered) or a specific list of user IDs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={form.targetType === 'all' ? 'default' : 'outline'}
              onClick={() => setForm({ ...form, targetType: 'all' })}
            >
              All users
            </Button>
            <Button
              type="button"
              size="sm"
              variant={form.targetType === 'userIds' ? 'default' : 'outline'}
              onClick={() => setForm({ ...form, targetType: 'userIds' })}
            >
              Specific user IDs
            </Button>
          </div>

          {form.targetType === 'userIds' ? (
            <div className="space-y-1.5">
              <Label htmlFor="user-ids">User IDs (comma or newline separated)</Label>
              <Textarea
                id="user-ids"
                value={form.userIdsInput}
                onChange={(e) => setForm({ ...form, userIdsInput: e.target.value })}
                rows={4}
                placeholder="abc123, def456, ..."
              />
            </div>
          ) : (
            <FiltersEditor
              filters={form.filters}
              onChange={(filters) => setForm({ ...form, filters })}
            />
          )}

          {form.targetType === 'all' && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  const res = await previewMut.mutateAsync(form.filters);
                  setPreviewResult(res);
                }}
                disabled={previewMut.isPending}
              >
                <Eye className="mr-1 h-3.5 w-3.5" />
                {previewMut.isPending ? 'Previewing…' : 'Preview audience'}
              </Button>
              {previewResult && (
                <Badge variant="outline">
                  {previewResult.matchCount.toLocaleString()} matched
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Preview</CardTitle>
          <CardDescription>Fully resolved content after variable substitution.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {resolvedContent.push && (
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Push
              </div>
              <div className="mt-1 rounded-md border p-3">
                <div className="font-medium">{resolvedContent.push.title}</div>
                <div className="text-muted-foreground">{resolvedContent.push.body}</div>
              </div>
            </div>
          )}
          {resolvedContent.inApp && (
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                In-app
              </div>
              <div className="mt-1 rounded-md border p-3">
                <div className="font-medium">{resolvedContent.inApp.title}</div>
                <div className="whitespace-pre-wrap text-muted-foreground">
                  {resolvedContent.inApp.body}
                </div>
              </div>
            </div>
          )}
          {!resolvedContent.push && !resolvedContent.inApp && (
            <p className="text-muted-foreground">No channel selected yet.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-wrap items-center justify-end gap-2 py-3">
          <Button variant="outline" onClick={() => navigate('/communication')}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit || createMut.isPending}
            onClick={async () => {
              const res = await createMut.mutateAsync(buildPayload());
              setLastCreatedId(res.campaign.campaignId);
            }}
          >
            <Send className="mr-1 h-3.5 w-3.5" />
            {createMut.isPending ? 'Creating…' : 'Create draft'}
          </Button>
        </CardContent>
      </Card>

      {lastCreatedId && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Draft created</CardTitle>
            <CardDescription>
              Test-send to up to 3 user IDs, or open the campaign detail to send/cancel.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="test-users">Test user IDs (up to 3, comma-separated)</Label>
              <Input
                id="test-users"
                value={testUserIds}
                onChange={(e) => setTestUserIds(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={testSendMut.isPending}
                onClick={() => {
                  const ids = testUserIds
                    .split(/[,\s]+/)
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .slice(0, 3);
                  if (ids.length === 0) return;
                  testSendMut.mutate({ campaignId: lastCreatedId, testUserIds: ids });
                }}
              >
                <Send className="mr-1 h-3.5 w-3.5" />
                {testSendMut.isPending ? 'Sending…' : 'Test send'}
              </Button>
              <Button
                type="button"
                onClick={() =>
                  navigate(`/communication/campaigns/${encodeURIComponent(lastCreatedId)}`)
                }
              >
                Open detail →
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function FiltersEditor({
  filters,
  onChange,
}: {
  filters: AudienceFilters;
  onChange: (f: AudienceFilters) => void;
}) {
  const setTriState = (key: 'hasPushToken' | 'hasTeam' | 'onboardingPassed' | 'hasCheckedIn' | 'isActive') =>
    (v: 'any' | 'yes' | 'no') => {
      const next = { ...filters };
      if (v === 'any') delete next[key];
      else next[key] = v === 'yes';
      onChange(next);
    };

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <TriSelect label="Has push token" value={filters.hasPushToken} onChange={setTriState('hasPushToken')} />
      <TriSelect label="In a team" value={filters.hasTeam} onChange={setTriState('hasTeam')} />
      <TriSelect
        label="Onboarding passed"
        value={filters.onboardingPassed}
        onChange={setTriState('onboardingPassed')}
      />
      <TriSelect
        label="Has checked in"
        value={filters.hasCheckedIn}
        onChange={setTriState('hasCheckedIn')}
      />
      <div className="space-y-1.5">
        <Label htmlFor="streak-min">Streak min</Label>
        <Input
          id="streak-min"
          type="number"
          min={0}
          value={filters.streakMin ?? ''}
          onChange={(e) => {
            const next = { ...filters };
            if (!e.target.value) delete next.streakMin;
            else next.streakMin = parseInt(e.target.value, 10);
            onChange(next);
          }}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="streak-max">Streak max</Label>
        <Input
          id="streak-max"
          type="number"
          min={0}
          value={filters.streakMax ?? ''}
          onChange={(e) => {
            const next = { ...filters };
            if (!e.target.value) delete next.streakMax;
            else next.streakMax = parseInt(e.target.value, 10);
            onChange(next);
          }}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="langs">Languages (comma-separated)</Label>
        <Input
          id="langs"
          value={(filters.languages ?? []).join(', ')}
          onChange={(e) => {
            const next = { ...filters };
            const parsed = e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            if (parsed.length === 0) delete next.languages;
            else next.languages = parsed;
            onChange(next);
          }}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="tzs">Timezones (comma-separated)</Label>
        <Input
          id="tzs"
          value={(filters.timezones ?? []).join(', ')}
          onChange={(e) => {
            const next = { ...filters };
            const parsed = e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            if (parsed.length === 0) delete next.timezones;
            else next.timezones = parsed;
            onChange(next);
          }}
        />
      </div>
    </div>
  );
}

function TriSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | undefined;
  onChange: (v: 'any' | 'yes' | 'no') => void;
}) {
  const current = value === undefined ? 'any' : value ? 'yes' : 'no';
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={current} onValueChange={(v) => onChange(v as 'any' | 'yes' | 'no')}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">Any</SelectItem>
          <SelectItem value="yes">Yes</SelectItem>
          <SelectItem value="no">No</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
